import { Component, OnInit, computed, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { widgetsMapping } from 'dashboard/constants/visuals-dashboard/widget-component-mapping';
import { IceWidgetGroupsEnum } from 'dashboard/enums/visuals/ice-widget-groups.enum';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { camelCase } from 'lodash-es';
import { BaseModal } from 'shared/abstract/base-modal';
import { VisualDashboardItemPickerItemComponent } from './visual-dashboard-item-picker-item/visual-dashboard-item-picker-item.component';

const ALL_VALUE = 'all';

@Component({
    selector: 'app-visual-dashboard-item-picker-modal',
    imports: [TranslateModule, VisualDashboardItemPickerItemComponent, ReactiveFormsModule],
    templateUrl: './visual-dashboard-item-picker-modal.component.html',
    styleUrl: './visual-dashboard-item-picker-modal.component.scss',
})
export class VisualDashboardItemPickerModalComponent extends BaseModal implements OnInit {
    data!: any;

    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;

    searchForm = new FormGroup({
        searchTerm: new FormControl(''),
        filterValue: new FormControl(ALL_VALUE),
    });

    widgetSelectionTranslationKey = computed(() => {
        const numberOfWidgets = this.visualsDashboardService.selectedWidgetsForAdding().length;
        if (numberOfWidgets === 0) {
            return 'dashboard.visuals.addModal.widgetSelection.none';
        }
        if (numberOfWidgets === 1) {
            return 'dashboard.visuals.addModal.widgetSelection.singular';
        }
        return 'dashboard.visuals.addModal.widgetSelection.plural';
    });

    filterOptions = signal<{ label: string; value: string | null }[]>([]);

    searchControlValue = toSignal(this.searchForm.get('searchTerm')!.valueChanges, { initialValue: '' });

    filterControlValue = toSignal(this.searchForm.get('filterValue')!.valueChanges, { initialValue: ALL_VALUE });
    widgetsList = computed(() => {
        const widgets = this.visualsDashboardService.mappedWidgetsList();
        const implementedWidgets = widgetsMapping;
        const selectedWidgets = this.visualsDashboardService.selectedWidgetsForAdding();
        const dashboardWidgets = this.visualsDashboardService.dashboard();
        const widgetsList = widgets
            .map((widget) => ({
                ...widget,
                isSelected: selectedWidgets.some((selectedWidget) => selectedWidget.widget.widgetId === widget.widgetId),
                isAddedToDashboard: dashboardWidgets.some(
                    (dashboardWidget) => dashboardWidget.iceDashboardWidget.widgetId === widget.widgetId,
                ),
            }))
            .filter((widget) => implementedWidgets[widget.key]);

        const searchTerm = this.searchControlValue()?.toLowerCase().trim();
        const filterValue = this.filterControlValue();

        return widgetsList.filter((widget) => {
            const matchesSearch = !searchTerm || widget.searchableText.includes(searchTerm);
            const matchesFilter = filterValue === ALL_VALUE || widget.iceListWidget.group.name === filterValue;
            return matchesSearch && matchesFilter;
        });
    });

    constructor(public visualsDashboardService: VisualsDashboardService) {
        super();
    }

    clearSearch() {
        this.searchForm.patchValue({ searchTerm: '' });
    }

    ngOnInit(): void {
        this.generateFilterOptions();
    }

    generateFilterOptions() {
        const groups = IceWidgetGroupsEnum;
        const values = Object.values(groups);
        const filterOptions: { label: string; value: string | null }[] = values.map((group) => ({
            label: `dashboard.visuals.addModal.groups.${camelCase(group)}`,
            value: group,
        }));
        filterOptions.unshift({ label: 'dashboard.visuals.addModal.groups.all', value: ALL_VALUE });
        this.filterOptions.set(filterOptions);
    }

    public closeModal() {
        this.closeModalInstance(undefined);
    }

    public confirmSelection() {
        this.closeModalInstance(this.visualsDashboardService.selectedWidgetsForAdding());
    }
}
